import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Barcode, Search } from "lucide-react";

export const FixBarcodeDialog = ({
  open, onOpenChange,
  fixBarcodePlu, setFixBarcodePlu,
  fixBarcodeProduct, setFixBarcodeProduct,
  fixBarcodeResults,
  fixBarcodeLoading,
  fixBarcodeNewBarcode, setFixBarcodeNewBarcode,
  fixBarcodeSaving,
  onLookup, onSave, onClose,
  focusSearchInput,
}) => {
  const showResultsList = !fixBarcodeProduct && Array.isArray(fixBarcodeResults) && fixBarcodeResults.length > 0;

  return (
    <Dialog open={open} onOpenChange={(o) => {
      onOpenChange(o);
      if (!o) focusSearchInput();
    }}>
      <DialogContent className="max-w-md" data-testid="fix-barcode-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Barcode className="h-5 w-5 text-amber-600" /> Fix Product Barcode
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Step 1: Find product (PLU code or description)</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  data-testid="fix-barcode-search-input"
                  value={fixBarcodePlu}
                  onChange={(e) => {
                    setFixBarcodePlu(e.target.value);
                    if (fixBarcodeProduct) setFixBarcodeProduct(null);
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter') onLookup(); }}
                  placeholder="PLU code or product name…"
                  className="pl-9"
                  autoFocus
                  disabled={fixBarcodeLoading}
                />
              </div>
              <Button
                data-testid="fix-barcode-find-btn"
                onClick={onLookup}
                disabled={!fixBarcodePlu.trim() || fixBarcodeLoading}
              >
                {fixBarcodeLoading ? "Looking up…" : "Find"}
              </Button>
            </div>
            <p className="text-xs text-gray-500">Tip: type any part of the description (e.g. "taper plug") and pick the right one from the list.</p>
          </div>

          {showResultsList && (
            <div className="border rounded-lg max-h-60 overflow-auto divide-y" data-testid="fix-barcode-results-list">
              {fixBarcodeResults.map((p) => (
                <button
                  key={p.id}
                  data-testid={`fix-barcode-result-${p.plu_code || p.id}`}
                  type="button"
                  onClick={() => {
                    setFixBarcodeProduct(p);
                    setFixBarcodeNewBarcode(p.barcode || "");
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-amber-50 transition-colors"
                >
                  <div className="font-medium text-sm">{p.name}</div>
                  <div className="text-xs text-gray-500">
                    PLU: <span className="font-mono">{p.plu_code || "—"}</span>
                    {" · "}Barcode: <span className="font-mono">{p.barcode || "none"}</span>
                  </div>
                </button>
              ))}
            </div>
          )}

          {fixBarcodeProduct && (
            <>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3" data-testid="fix-barcode-selected-product">
                <p className="font-semibold text-green-800">{fixBarcodeProduct.name}</p>
                <p className="text-sm text-green-600">
                  PLU: {fixBarcodeProduct.plu_code} | Current Barcode: {fixBarcodeProduct.barcode || "None"}
                </p>
              </div>
              <div className="space-y-2">
                <Label>Step 2: Enter Correct Barcode</Label>
                <Input
                  data-testid="fix-barcode-new-barcode-input"
                  value={fixBarcodeNewBarcode}
                  onChange={(e) => setFixBarcodeNewBarcode(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') onSave(); }}
                  placeholder="Scan or enter barcode..."
                  className="text-lg font-mono"
                />
                <p className="text-xs text-gray-500">Tip: You can scan the barcode directly into this field</p>
              </div>
            </>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="fix-barcode-cancel-btn">Cancel</Button>
          {fixBarcodeProduct && (
            <Button
              data-testid="fix-barcode-save-btn"
              onClick={onSave}
              disabled={!fixBarcodeNewBarcode.trim() || fixBarcodeSaving}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {fixBarcodeSaving ? "Saving..." : "Save Barcode"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
